import React from "react";
import styled from "styled-components";

import {
  CANVAS_HEIGHT,
  CANVAS_WIDTH,
  DEFAULT_EGG_LIGHT,
  MAX_EGG_LIGHTS,
} from "../../constants";
import ToolSlider from "../ToolSlider";

// Multi-light direction + intensity + shadow + size editor.
// Drop-in popover-body for any panel that wants the full lighting
// pipeline exposed (author + creative). Owns its own activeLight
// index (resets when the lights array shrinks below the index).
//
// Lights array shape (matching the saved-reference format):
//   [{ direction: { x, y, z }, intensity, shadow, size }]
// `setLights(next)` is called with a new array on every edit; the
// caller decides where the array lives (author state, creative state,
// etc).
//
// `onResetAll` is optional — when supplied, a "Reset all" button is
// shown alongside Remove. Author wires this to the per-marc-default
// reset; creative leaves it unset (the marc swap reseeds anyway).
function LightingEditor({ lights, setLights, onResetAll }) {
  const [activeLightIndex, setActiveLightIndex] = React.useState(0);
  const safeLights = Array.isArray(lights) ? lights : [];
  const safeLightIndex = Math.min(
    Math.max(0, activeLightIndex),
    Math.max(0, safeLights.length - 1),
  );
  const activeLight = safeLights[safeLightIndex] ?? DEFAULT_EGG_LIGHT;

  const updateActive = (patch) => {
    setLights(
      safeLights.map((L, i) =>
        i === safeLightIndex ? { ...(L ?? {}), ...patch } : L,
      ),
    );
  };
  const atMaxLights = safeLights.length >= MAX_EGG_LIGHTS;
  const addLight = () => {
    if (atMaxLights) return;
    const next = [...safeLights, { ...DEFAULT_EGG_LIGHT }];
    setLights(next);
    setActiveLightIndex(next.length - 1);
  };
  const removeActive = () => {
    if (safeLights.length === 0) return;
    const next = safeLights.filter((_, i) => i !== safeLightIndex);
    setLights(next);
    setActiveLightIndex(Math.min(safeLightIndex, Math.max(0, next.length - 1)));
  };

  return (
    <>
      <PopLabel>
        Light {safeLightIndex + 1} of {safeLights.length}
      </PopLabel>
      <Tabs>
        {safeLights.map((_, i) => (
          <TabButton
            key={i}
            type="button"
            $active={i === safeLightIndex}
            onClick={() => setActiveLightIndex(i)}
            aria-label={`Edit light ${i + 1}`}
          >
            {i + 1}
          </TabButton>
        ))}
        <TabButton
          type="button"
          onClick={addLight}
          disabled={atMaxLights}
          aria-label="Add light"
          title={atMaxLights ? `Max ${MAX_EGG_LIGHTS} lights` : "Add a new light"}
        >
          +
        </TabButton>
      </Tabs>
      <PopLabel>Direction</PopLabel>
      <LightPad
        lights={safeLights}
        activeIndex={safeLightIndex}
        onSelectLight={setActiveLightIndex}
        onChangeActive={(direction) => updateActive({ direction })}
      />
      <PopLabel>
        Intensity · {Math.round((activeLight?.intensity ?? 0) * 100)}
      </PopLabel>
      <ToolSlider
        min={0}
        max={100}
        value={Math.round((activeLight?.intensity ?? 0) * 100)}
        onValueChange={(v) => updateActive({ intensity: v / 100 })}
        ariaLabel="Light intensity"
      />
      <PopLabel>
        Shadow · {Math.round((activeLight?.shadow ?? 0) * 100)}
      </PopLabel>
      <ToolSlider
        min={0}
        max={100}
        value={Math.round((activeLight?.shadow ?? 0) * 100)}
        onValueChange={(v) => updateActive({ shadow: v / 100 })}
        ariaLabel="Light shadow"
      />
      <PopLabel>
        Size · {Math.round((activeLight?.size ?? 1) * 100)}
      </PopLabel>
      <ToolSlider
        min={0}
        max={100}
        value={Math.round((activeLight?.size ?? 1) * 100)}
        onValueChange={(v) => updateActive({ size: v / 100 })}
        ariaLabel="Light size"
      />
      <Row>
        <MiniButton
          type="button"
          onClick={removeActive}
          disabled={safeLights.length <= 1}
          title={
            safeLights.length <= 1
              ? "Need at least one light"
              : "Remove this light"
          }
        >
          Remove
        </MiniButton>
        {onResetAll && (
          <MiniButton type="button" onClick={onResetAll}>
            Reset all
          </MiniButton>
        )}
      </Row>
    </>
  );
}

// Drag-a-dot pad for the light source position. Rectangle is sized to the
// canvas aspect so the author can place the light spatially relative to
// where the egg lives. Mapping: pad position → 2D direction in [-1, 1]²,
// then clamped to the unit disk so the result lies on the upper
// hemisphere; z is derived as sqrt(1 - x² - y²). Center = head-on,
// rectangle edges = pure side light.
//
// Multi-light: every light renders as a dot. Active is the warn color
// and on top; inactive lights are muted and behind. Pointer-down on a
// non-active dot selects that light (and starts dragging it); pointer-
// down anywhere else drags the active dot to that point.
const PAD_DOT_HIT_RADIUS = 12;
function LightPad({ lights, activeIndex, onSelectLight, onChangeActive }) {
  const padRef = React.useRef(null);
  const dragRef = React.useRef(false);

  const aspect = CANVAS_WIDTH / CANVAS_HEIGHT;
  const padHeight = 140;
  const padWidth = padHeight * aspect;
  const halfW = padWidth / 2;
  const halfH = padHeight / 2;

  const dotPos = (L) => {
    const dx = L?.direction?.x ?? 0;
    const dy = L?.direction?.y ?? 0;
    return { x: halfW + dx * halfW, y: halfH + dy * halfH };
  };

  const setFromPointer = (event) => {
    const pad = padRef.current;
    if (!pad) return;
    const rect = pad.getBoundingClientRect();
    let x = (event.clientX - rect.left - halfW) / halfW;
    let y = (event.clientY - rect.top - halfH) / halfH;
    const r = Math.sqrt(x * x + y * y);
    if (r > 1) {
      x /= r;
      y /= r;
    }
    const z = Math.sqrt(Math.max(0, 1 - x * x - y * y));
    onChangeActive({ x, y, z });
  };

  const handleDown = (event) => {
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    dragRef.current = true;
    const pad = padRef.current;
    if (pad) {
      const rect = pad.getBoundingClientRect();
      const px = event.clientX - rect.left;
      const py = event.clientY - rect.top;
      let bestI = -1;
      let bestD2 = PAD_DOT_HIT_RADIUS * PAD_DOT_HIT_RADIUS;
      for (let i = 0; i < lights.length; i++) {
        if (i === activeIndex) continue;
        const { x, y } = dotPos(lights[i]);
        const d2 = (x - px) * (x - px) + (y - py) * (y - py);
        if (d2 < bestD2) {
          bestD2 = d2;
          bestI = i;
        }
      }
      if (bestI >= 0) {
        onSelectLight(bestI);
        return;
      }
    }
    setFromPointer(event);
  };
  const handleMove = (event) => {
    if (!dragRef.current) return;
    setFromPointer(event);
  };
  const handleUp = (event) => {
    dragRef.current = false;
    try {
      event.currentTarget.releasePointerCapture(event.pointerId);
    } catch {
      // ignore — pointer may have already been released
    }
  };

  return (
    <Pad
      ref={padRef}
      onPointerDown={handleDown}
      onPointerMove={handleMove}
      onPointerUp={handleUp}
      onPointerCancel={handleUp}
      style={{ width: padWidth, height: padHeight }}
    >
      <PadEgg />
      {lights.map((L, i) => {
        if (i === activeIndex) return null;
        const { x, y } = dotPos(L);
        return (
          <PadDot
            key={i}
            $inactive
            style={{ left: x, top: y }}
            aria-label={`Light ${i + 1}`}
          >
            {i + 1}
          </PadDot>
        );
      })}
      {lights[activeIndex] && (
        <PadDot
          style={{
            left: dotPos(lights[activeIndex]).x,
            top: dotPos(lights[activeIndex]).y,
          }}
          aria-label={`Light ${activeIndex + 1} (active)`}
        >
          {activeIndex + 1}
        </PadDot>
      )}
    </Pad>
  );
}

const PopLabel = styled.div`
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--color-text-muted);
`;

const Row = styled.div`
  display: flex;
  gap: 6px;
`;

const Tabs = styled.div`
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
`;

const TabButton = styled.button`
  min-width: 24px;
  padding: 2px 8px;
  border-radius: 4px;
  border: 1px solid
    ${(p) => (p.$active ? "var(--color-warn)" : "var(--color-border)")};
  background: ${(p) =>
    p.$active ? "var(--color-warn)" : "var(--color-surface)"};
  color: ${(p) => (p.$active ? "var(--color-text)" : "inherit")};
  font-size: 11px;
  font-weight: ${(p) => (p.$active ? "700" : "400")};
  cursor: pointer;
  font-variant-numeric: tabular-nums;

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const Pad = styled.div`
  position: relative;
  border: 1px solid var(--color-border);
  border-radius: 4px;
  background: var(--color-surface);
  cursor: crosshair;
  touch-action: none;
  user-select: none;
`;

const PadEgg = styled.div`
  position: absolute;
  left: 15%;
  right: 15%;
  top: 15%;
  bottom: 15%;
  border-radius: 50%;
  background: var(--color-border-subtle);
  pointer-events: none;
`;

const PadDot = styled.div`
  position: absolute;
  width: 16px;
  height: 16px;
  margin-left: -8px;
  margin-top: -8px;
  border-radius: 50%;
  background: ${(p) =>
    p.$inactive ? "var(--color-text-muted, #999)" : "var(--color-warn)"};
  border: 2px solid var(--color-text);
  opacity: ${(p) => (p.$inactive ? 0.6 : 1)};
  z-index: ${(p) => (p.$inactive ? 1 : 2)};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 9px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: var(--color-text);
  pointer-events: none;
`;

const MiniButton = styled.button`
  padding: 4px 8px;
  border-radius: 4px;
  border: 1px solid var(--color-border);
  background: var(--color-surface);
  font-size: 11px;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export default LightingEditor;
